import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useSupplierBrandColor } from 'modules/FreeSupplier/useSupplierPublication';
import EmbedSnippetPanel from 'modules/PublicCatalog/studio/EmbedSnippetPanel';
import { useMockData } from '../state/MockDataContext';
import UpsellCard from '../components/UpsellCard';
import { useUpgradeModal } from '../components/UpgradeModal';
import { trackFreeSupplierWidgetCopied } from 'helpers/mixpanel';

// The free-tier embed page. The configurator itself (controls, snippet, live
// preview, how-to) is the shared EmbedSnippetPanel — this file owns only what is
// supplier-specific: the MockData-backed widget prefs, the free-tier upsells,
// and the free-supplier analytics event.

// Locked exposure-analytics teaser — fake sparkline + stats behind a lock.
// Deferred capability (allowed to stay as-is, unlike the SEFA teasers — D-S9-6).
function LockedAnalyticsPanel() {
  const { t } = useTranslation('freeSupplier');
  const { openUpgrade } = useUpgradeModal();
  const teaserStats = [
    { value: '128', label: t('freeSupplier:embedWidget.teaser.widgetViews', 'widget views') },
    { value: '12', label: t('freeSupplier:embedWidget.teaser.downloads', 'downloads') },
    { value: '3', label: t('freeSupplier:embedWidget.teaser.dataRequests', 'data requests') },
  ];
  return (
    <button
      type="button"
      onClick={() => openUpgrade('insights')}
      className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left hover:border-primaryColor/30 transition-colors"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 m-0">
          {t(
            'freeSupplier:embedWidget.teaser.heading',
            'CBAM Portal & widget analytics — last 30 days',
          )}
        </p>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primaryColor">
          <FontAwesomeIcon
            icon={faLock}
            className="text-[9px]"
          />
          {t('freeSupplier:embedWidget.teaser.premium', 'Premium')}
        </span>
      </div>
      <div className="flex items-center gap-4 blur-[2px] select-none pointer-events-none">
        <svg
          viewBox="0 0 120 32"
          className="w-28 h-8 shrink-0"
          aria-hidden="true"
        >
          <polyline
            points="0,26 15,22 30,24 45,16 60,18 75,10 90,13 105,6 120,8"
            fill="none"
            stroke="rgb(var(--primary-color))"
            strokeWidth="2"
          />
        </svg>
        {teaserStats.map((s) => (
          <span key={s.label}>
            <span className="text-base font-bold text-slate-800">{s.value}</span>
            <span className="text-[10px] text-slate-400 block leading-tight">{s.label}</span>
          </span>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 m-0 mt-2">
        {t(
          'freeSupplier:embedWidget.teaser.footer',
          "See who's viewing your data and turn exposure into orders — included in Premium insights.",
        )}
      </p>
    </button>
  );
}

// D-S9-9: shown when the supplier hasn't published a Supplier Page yet (or has
// disabled it) — a widget has nothing to embed without one.
function PublishFirstState() {
  const { t } = useTranslation('freeSupplier');
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-center gap-3">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        className="text-amber-500"
      />
      <p className="text-xs text-slate-700 m-0 flex-1">
        {t(
          'freeSupplier:embedWidget.publishFirst.body',
          'Publish your CBAM Portal first — the widget shows the same public data.',
        )}
      </p>
      <Link
        to="/supplier/share"
        className="text-xs font-semibold text-primaryColor no-underline hover:underline shrink-0"
      >
        {t('freeSupplier:embedWidget.publishFirst.cta', 'Publish your CBAM Portal →')}
      </Link>
    </div>
  );
}

export default function EmbedWidgetPage() {
  const { t } = useTranslation('freeSupplier');
  const { data, updateWidget } = useMockData();
  const { widget, company, share } = data;
  // The persisted brand color (D50) — the SAME shared control the Share & Portal
  // page uses, so both pages edit one value identically.
  const { effectiveColor, persistColor, saving: colorSaving } = useSupplierBrandColor();

  const canEmbed = !!(share.enabled && share.slug);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800 m-0">
          {t('freeSupplier:embedWidget.title', 'Embed Widget')}
        </h1>
        <p className="text-sm text-slate-500 mt-1 m-0">
          {t(
            'freeSupplier:embedWidget.subtitle',
            "Put your CBAM Portal on your own website — customers see you're CBAM-ready before they even ask.",
          )}
        </p>
      </div>

      {!canEmbed ? (
        <PublishFirstState />
      ) : (
        <>
          <EmbedSnippetPanel
            slug={share.slug}
            companyName={company.name}
            effectiveColor={effectiveColor}
            persistColor={persistColor}
            colorSaving={colorSaving}
            prefs={widget}
            onPrefsChange={updateWidget}
            idPrefix="cg-supplier-widget"
            onCopied={({ layout, theme }) => {
              updateWidget({ copiedOnce: true });
              trackFreeSupplierWidgetCopied({ layout, theme });
            }}
          >
            <LockedAnalyticsPanel />
          </EmbedSnippetPanel>

          <UpsellCard
            feature="costs"
            title={t(
              'freeSupplier:embedWidget.upsell.title',
              'Show CBAM cost estimates in your widget',
            )}
            body={t(
              'freeSupplier:embedWidget.upsell.body',
              'Premium widgets can include per-good CBAM cost estimates and a verified badge — even more reason for buyers to pick you.',
            )}
            ctaLabel={t('freeSupplier:embedWidget.upsell.cta', 'See Premium widgets')}
            dismissKey="widget-costs"
          />
        </>
      )}
    </div>
  );
}
